import { Paper, Text, Group, createStyles, Box, LoadingOverlay, UnstyledButton, Modal, useMantineTheme } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useDasboardApi from '../../hooks/useDashboardApi';
import TimeSelector from './card-components/TimeSelector';
import { MapContainer, TileLayer } from 'react-leaflet';
import FilterSelector from './selectors/FilterSelector';
import { useDisclosure } from '@mantine/hooks';
import {HeatmapLayer} from 'react-leaflet-heatmap-layer-v3';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: '20px 0 10px'
	},
	text: {
		fontSize: 36,
		lineHeight: '42px',
		fontWeight: 300,
	},
	cardTitle: {
		fontWeight: 600,
		fontSize: 16,
		lineHeight: '20px',
		color: theme.colorScheme === 'light' ? '#4A4A4A' : '#C1C2C5',
	},
	descriptionText: {
		fontSize: 12,
		lineHeight: '14px',
		fontWeight: 300,
		letterSpacing: 1.1
	}
}));

export default function UserMap() {

	// State and hooks
	const { classes } = useStyles();
	const [selectedRange, setSelectedRange] = useState('total');
	const [range, setRange] = useState([null, null]);
	const [visible, setVisible] = useState(false);
	const { getUserActivityZones } = useDasboardApi();
	const { userZoneActivity } = useSelector(state => state.dashboardData);
	const [opened, { close, open }] = useDisclosure(false);
	const [filters, setFilters] = useState([]);
	const theme = useMantineTheme();
	const [heatMapPoints, setHeatMapPoints] = useState([])

	// On load
	useEffect(() => {
		async function fetchData() {
			await getUserActivityZones('', new Date(), filters);
		}
		fetchData();
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if(userZoneActivity && userZoneActivity.length) {

			// Preprocesar las intensidades para cada punto de userZoneActivity
			const processedPoints = userZoneActivity.map((m) => {
				const intensity = userZoneActivity.filter(coord => distance(coord, m) <= 500).length;
				return { lat: m[1], lng: m[0], intensity };
			});
			
			// Extraer puntos listos para el Heatmap
			const heatmapPoints = processedPoints.map(({ lat, lng, intensity }) => [lng, lat, intensity / Math.max(...processedPoints.map(p => p.intensity))]);
			setHeatMapPoints(heatmapPoints)
		}
	}, [userZoneActivity])

	// Listen to selected range changes
	useEffect(() => {

		// Only if initial data has loaded
		if (userZoneActivity !== null) reload();

		// eslint-disable-next-line
	}, [selectedRange, filters]);

	// Reload with new values
	const reload = async () => {

		// Set loader
		setVisible(true);

		// Define date variables
		let endDate = new Date();
		let startDate = new Date();

		// Check date range to apply
		switch (selectedRange) {
			case '30d':
				startDate.setDate(endDate.getDate() - 30);
				break;
			case '7d':
				startDate.setDate(endDate.getDate() - 7);
				break;
			case 'total':
				// Default start date
				startDate = '';
				break;
			case 'custom':
				// Assign custom range
				endDate = range[1];
				startDate = range[0];
				break;
			default:
				// Default start date
				startDate = '';
				break;
		}

		// Get information
		if (selectedRange) await getUserActivityZones(startDate, endDate, filters);

		// Remove loader
		setVisible(false);

	}

	const distance = (coord1, coord2) => {
		const R = 6371000; // Radio de la Tierra en metros
		const dLat = (coord2[1] - coord1[1]) * (Math.PI / 180);
		const dLon = (coord2[0] - coord1[0]) * (Math.PI / 180);
		const a = 
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(coord1[1] * (Math.PI / 180)) * Math.cos(coord2[1] * (Math.PI / 180)) *
			Math.sin(dLon / 2) * Math.sin(dLon / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		return R * c; // Distancia en metros
	};

	// Map points styles
	const polygonStyle = {
		color: 'transparent',
		fillColor: '#9B49DA',
		fillOpacity: 0.5,
	};

	return (
		<>
			{/* Map Modal */}
			<Modal
				opened={opened}
				onClose={close}
				size="100%"
				centered
			>
				{/* Map */}
				<MapContainer
					center={[-34.59617428960621, -58.44402396099564]}
					zoom={13}
					style={{ height: '60vh' }}
				>
					<TileLayer
						attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>
					{/* Coordinates */}
					{heatMapPoints && heatMapPoints?.length > 0 && 
						<HeatmapLayer
						points={heatMapPoints}
						longitudeExtractor={(m) => m[0]}
						latitudeExtractor={(m) => m[1]}
						intensityExtractor={(m) =>m[2]}
						max={1}
						minOpacity={0.2}
						blur={15}      
     					radius={25} 
						/>}

    
				</MapContainer>
			</Modal>
			{/* Container */}
			<Paper
				withBorder
				p={12}
				style={{ position: 'relative' }}
				mt={30}
				radius={18}
			>
				{/* Loading Overlay */}
				<LoadingOverlay
					visible={visible}
					overlayBlur={1}
					radius={18}
				/>
				{/* Top */}
				<Group
					position="apart"
					noWrap
				>
					{/* Title */}
					<Text className={classes.cardTitle}>
						Actividad por Zona
					</Text>
				</Group>
				{/* Time & Filter */}
				<Group
					position="apart"
					mt={8}
					noWrap
					align="center"
				>
					{/* Range */}
					<TimeSelector
						data={userZoneActivity}
						selectedRange={selectedRange}
						setSelectedRange={setSelectedRange}
						range={range}
						setRange={setRange}
					/>
					{/* Filter */}
					<FilterSelector
						label="Días"
						options={[
							{
								name: 'Lunes',
								id: 0,
							},
							{
								name: 'Martes',
								id: 1,
							},
							{
								name: 'Miércoles',
								id: 2,
							},
							{
								name: 'Jueves',
								id: 3,
							},
							{
								name: 'Viernes',
								id: 4,
							},
							{
								name: 'Sábado',
								id: 5,
							},
							{
								name: 'Domingo',
								id: 6,
							},
						]}
						selected={[filters, setFilters]}
					/>
				</Group>
				{/* Content */}
				<Box className={classes.container}>
					{/* Map */}
					<MapContainer center={[-34.59617428960621, -58.44402396099564]} zoom={13} >
						<TileLayer
							attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
							url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
						/>
						{/* Coordinates */}
						{heatMapPoints && heatMapPoints?.length > 0 && 
							<HeatmapLayer
							points={heatMapPoints}
							longitudeExtractor={(m) => m[0]}
							latitudeExtractor={(m) => m[1]}
							intensityExtractor={(m) =>m[2]}
							max={1}
							minOpacity={0.2}
							blur={15}      
							radius={25} 
							/>}
					</MapContainer>
				</Box>
				{/* Expand */}
				<Group position="right">
					{/* Activator */}
					<UnstyledButton onClick={open}>
						{/* Text */}
						<Text
							c={theme.colorScheme === 'dark' ? theme.colors.gray[0] : 'mainPurple'}
							fz={14}
							td="underline"
						>
							Expand
						</Text>
					</UnstyledButton>
				</Group>
			</Paper>
		</>
	);

}
